import React from 'react';
import robot from './assets/generic_error_robot.png';
import styled from '@emotion/styled';
import { BodyTextMedium, HeadingText50 } from '@modernhealth/vital-components';
import i18n from 'src/config/i18n';
import { captureException } from 'src/shared/helpers/captureException';

interface State {
    hasError: boolean;
}

enum ErrorBoundaryTestID {
    Wrapper = 'ErrorBoundaryTestID.Wrapper',
}

const ResourceContainer = styled.div({
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
});

const SolutionNumberWrapper = styled.div({
    borderWidth: '1px',
    borderRadius: '20px',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '15px',
    borderStyle: 'solid',
});

const SolutionContainer = styled.div({
    marginTop: '16px',
    marginBottom: '16px',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
});

class ErrorBoundary extends React.Component {
    public state: State = { hasError: false };

    public static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    public componentDidCatch(error: Error): void {
        captureException(error);
    }

    public render(): JSX.Element | React.ReactNode {
        if (this.state.hasError) {
            if (!i18n.isInitialized) {
                return (
                    <ResourceContainer data-testid={ErrorBoundaryTestID.Wrapper}>
                        <img src={robot} alt="error-page" />
                    </ResourceContainer>
                );
            }

            const t = i18n.t.bind(i18n);

            return (
                <ResourceContainer data-testid={ErrorBoundaryTestID.Wrapper}>
                    <img src={robot} alt="error-page" />

                    <HeadingText50 marginTop={16}>{t('shared:error_boundary.title')}</HeadingText50>

                    <BodyTextMedium>{t('shared:error_boundary.recommendations')}</BodyTextMedium>

                    <SolutionContainer>
                        <SolutionNumberWrapper>1</SolutionNumberWrapper>

                        <BodyTextMedium underline>
                            {t('shared:error_boundary.refresh_recommendation')}
                        </BodyTextMedium>
                    </SolutionContainer>

                    <BodyTextMedium>{t('shared:error_boundary.contact')}</BodyTextMedium>
                    <BodyTextMedium>
                        <a href="mailto:help@joinmodernhealth.com">help@joinmodernhealth.com</a>
                    </BodyTextMedium>
                </ResourceContainer>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
